import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useForm } from 'react-hook-form';

import { Box, CircularProgress } from '@mui/material';

import type { CreateTagAssignmentInput, UpdateTagInput } from '~/services/TagsService';

import type { AssignmentFormData } from './TagAssignments/TagAssignments';
import TagForm, { type FormTag } from './TagForm';
import { useGetTag, useUpdateTag } from './hooks';
import { useCreateTagAssignments } from './hooks/useTagAssignments';
import { useTagAssignmentsForForm } from './hooks/useTagAssignmentsForForm';

interface Props {
  tagId: string;
  onBack: () => void;
}

export default function TagEdit({ tagId, onBack }: Props) {
  const [tab, setTab] = useState('assignments');
  const [assignments, setAssignments] = useState<Array<AssignmentFormData>>([]);
  const initialAssignmentsRef = useRef<Array<AssignmentFormData>>([]);

  const { data: tag = null, isLoading: isLoadingTag = false } = useGetTag(tagId);
  const editTag = useUpdateTag();
  const createTagAssignments = useCreateTagAssignments();

  const tagAssignmentsData = useTagAssignmentsForForm({
    tagId,
  });
  const savedAssignments = tagAssignmentsData.assignments;
  const isLoadingAssignments = tagAssignmentsData.isLoading;

  const { control, handleSubmit, reset, formState } = useForm<FormTag>({
    defaultValues: {
      name: '',
      schedules: [],
    },
  });

  useEffect(() => {
    if (tag && savedAssignments) {
      const formSchedules = (tag.schedules || []).map((schedule) => ({
        brands: schedule.brands,
        activeStartDate: schedule.activeStartDate,
        activeEndDate: schedule.activeEndDate,
      }));
      reset({
        name: tag.name,
        schedules: formSchedules,
      });
      setAssignments(savedAssignments);
      initialAssignmentsRef.current = savedAssignments;
    }
  }, [tag, savedAssignments, reset]);

  const assignmentsToAdd = useMemo(() => {
    const initial = initialAssignmentsRef.current;
    const current = assignments;

    const initialMap = new Map(initial.map((a) => [`${a.entityType}-${a.entityId}`, a]));

    return current.filter((a) => !initialMap.has(`${a.entityType}-${a.entityId}`));
  }, [assignments]);

  const handleSave = async (data: FormTag) => {
    const updateData: UpdateTagInput = {
      name: data.name,
    };
    await editTag.mutateAsync({ id: tagId, data: updateData });
    if (assignmentsToAdd.length > 0) {
      const assignmentsToCreate: Array<CreateTagAssignmentInput> = assignmentsToAdd.map((assignment) => ({
        tagId,
        entityType: assignment.entityType,
        entityId: assignment.entityId,
      }));
      await createTagAssignments.mutateAsync(assignmentsToCreate);
    }
    onBack();
  };

  if (isLoadingTag || isLoadingAssignments) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TagForm
      mode="edit"
      tag={tag}
      tab={tab}
      control={control}
      formState={formState}
      assignments={assignments}
      onAssignmentsChange={setAssignments}
      initialAssignments={initialAssignmentsRef.current}
      isSaving={editTag.isPending || createTagAssignments.isPending}
      onBack={onBack}
      onSave={handleSubmit(handleSave)}
      onTabChange={setTab}
    />
  );
}
